import {
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import {
  ChangeEvent,
  forwardRef,
  InputHTMLAttributes,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'

import { cn } from '@/app/utils'

import { useMinMaxLengthMessage } from '../hooks/useMinMaxLengthMessage'
import AppInputMessage from './AppInputMessage'
import MinMaxLengthMessage from './MinMaxLengthMessage'

export type Props = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'data-testid'
> & {
  invalid?: boolean
  invalidMessage?: ReactNode
  inputClassName?: string
  rootClassName?: string
  maxLengthMessageFrom?: number
  extraContent?: ReactNode
  dataTestId?: string
  clearable?: boolean
  onClear?: () => void
}

// не пугаться! тут мы выставляем значение через нативный сеттер и диспатчим input-событие,
// чтобы очистку подхватили и React onChange, и IMask, и react-hook-form
const setNativeInputValue = (input: HTMLInputElement, value: string) => {
  const valueSetter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  )?.set
  valueSetter?.call(input, value)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

const AppInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      rootClassName,
      className,
      invalid,
      invalidMessage,
      type,
      inputClassName,
      placeholder,
      minLength,
      maxLength,
      maxLengthMessageFrom,
      onFocus,
      onBlur,
      extraContent,
      dataTestId,
      clearable = true,
      onClear,
      onChange,
      ...props
    },
    ref
  ) => {
    const _id = useId()

    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    const [hasValue, setHasValue] = useState(false)

    useEffect(() => {
      setHasValue(Boolean(inputRef.current?.value))
    }, [props.value])

    const showClearButton =
      clearable &&
      !extraContent &&
      hasValue &&
      !props.disabled &&
      !props.readOnly

    const isSearchInput = type === 'search'
    const isPasswordInput = type === 'password'

    const [isPasswordVisible, setIsPasswordVisible] = useState(false)

    const controlledInputType = useMemo(() => {
      const currentInputType = isPasswordVisible ? 'text' : 'password'
      return isPasswordInput ? currentInputType : type
    }, [isPasswordInput, isPasswordVisible, type])

    const {
      valueLength,
      isMinLengthInvalid,
      showMinLengthMessageValid,
      showMaxLengthMessageValid,
      setInputFocused
    } = useMinMaxLengthMessage({
      value: props.value,
      minLength,
      maxLengthMessageFrom
    })

    const currentBorderColor =
      invalid || isMinLengthInvalid ? '!border-red-80' : 'border-transparent'

    const handleChangeIsPasswordVisible = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        setIsPasswordVisible(prev => !prev)
      },
      []
    )

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setHasValue(Boolean(event.target.value))
      onChange?.(event)
    }

    const handleClear = () => {
      const input = inputRef.current
      if (!input) {
        return
      }

      setNativeInputValue(input, '')
      input.focus()
      onClear?.()
    }

    return (
      <div className={cn('w-full', rootClassName)}>
        <div
          data-component="AppInput"
          data-testid={dataTestId}
          className={cn(
            'relative flex h-56 items-end overflow-hidden rounded-12 border-2 bg-white font-pro md:h-48',
            currentBorderColor,
            {
              '!border-grey-100/[5%] bg-grey-10': props.disabled
            },
            className
          )}
        >
          <input
            id={_id}
            ref={inputRef}
            data-testid={
              dataTestId && `${dataTestId.replace(/-id$/, '')}-field-id`
            }
            className={cn(
              'peer flex flex-grow items-end',
              'px-12 py-6 text-deepBlack outline-none transition text-16 md:py-2',
              'placeholder:opacity-0',
              '[&::-webkit-search-cancel-button]:appearance-none',
              { 'pl-38': isSearchInput },
              { 'pr-30': isPasswordInput },
              { 'pr-40': showClearButton },
              { 'pr-[66px]': showClearButton && isPasswordInput },
              { 'bg-transparent': props.disabled },
              inputClassName
            )}
            placeholder={placeholder}
            type={controlledInputType}
            minLength={minLength}
            maxLength={maxLength}
            onFocus={event => {
              setInputFocused(true)
              onFocus?.(event)
            }}
            onBlur={event => {
              setInputFocused(false)
              onBlur?.(event)
            }}
            onChange={handleChange}
            {...props}
          />
          {isSearchInput && (
            <MagnifyingGlassIcon
              width={16}
              className={cn(
                'absolute left-10 top-27 translate-y-0 transition-all md:top-22',
                'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2',
                'peer-focus:top-27 peer-focus:translate-y-0 md:peer-focus:top-22'
              )}
            />
          )}
          <label
            htmlFor={_id}
            className={cn(
              'pointer-events-none absolute top-5 translate-y-0 text-black opacity-50 transition-all text-12',
              'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-16',
              !props.readOnly &&
                'peer-focus:top-5 peer-focus:translate-y-0 peer-focus:text-12',
              isSearchInput
                ? 'left-38 peer-focus:left-12 peer-[:not(:placeholder-shown)]:left-12'
                : 'left-12'
            )}
          >
            {placeholder}
          </label>
          <div className="absolute right-10 flex h-full items-center justify-center gap-8">
            {showClearButton && (
              <button
                type="button"
                aria-label="Очистить"
                data-testid={
                  dataTestId && `${dataTestId.replace(/-id$/, '')}-clear-id`
                }
                className="flex h-full select-none items-center justify-center"
                onMouseDown={event => event.preventDefault()}
                onClick={handleClear}
              >
                <XMarkIcon className="h-24 w-24 text-grey-40" />
              </button>
            )}
            {isPasswordInput && (
              <button
                type="button"
                aria-label={
                  isPasswordVisible ? 'Скрыть пароль' : 'Показать пароль'
                }
                className="flex h-full w-24 select-none items-center justify-center"
                onClick={handleChangeIsPasswordVisible}
              >
                {isPasswordVisible ? (
                  <EyeIcon width={16} />
                ) : (
                  <EyeSlashIcon width={16} />
                )}
              </button>
            )}
            {extraContent}
          </div>
        </div>
        {invalid ? (
          <AppInputMessage
            message={invalidMessage}
            iconClassName="text-red-70"
            messageClassName="text-red-70"
          />
        ) : (
          <>
            {Boolean(minLength) && (
              <MinMaxLengthMessage
                valueLength={valueLength}
                minLength={minLength}
                isLengthInvalid={isMinLengthInvalid}
                showMessageValid={showMinLengthMessageValid}
              />
            )}
            {Boolean(maxLength) && (
              <MinMaxLengthMessage
                valueLength={valueLength}
                maxLength={maxLength}
                showMessageValid={showMaxLengthMessageValid}
              />
            )}
          </>
        )}
      </div>
    )
  }
)

AppInput.displayName = 'AppInput'
export default AppInput
